import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import isClient from 'commons/isClient';
import { searchClient } from 'components/essentials/AlgoliaSearchClient';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';
import IconClose from 'components/icons/iconClose';
import MobileOnly from 'components/consumer/MobileOnly';
import TabletAndDesktopOnly from 'components/consumer/TabletAndDesktopOnly';
import ShopByTitleNavigation from 'components/consumer/ShopByTitleNavigation';
import { InstantSearch, Configure } from 'react-instantsearch-dom';
import useFilters from '../hooks/useFilters';
import GridWithSidebar from '../../GridWithSidebar';
import {
  InspirationSearchBox,
  InspirationSearchOptions,
} from '../search-state/utils';
import InspirationFiltersTop from '../inspiration-filters/InspirationFiltersTop';
import CustomerPhotosGrid from '../components/CustomerPhotosGrid';
import useUrlPhotoSelection from '../hooks/useUrlPhotoSelection';
import useProductFilterState from '../hooks/useProductFilterState';
import useProductSearch from '../hooks/useProductSearch';
import useCustomerPhotosFilterActions from '../hooks/useCustomerPhotosFilterActions';
import MobileInspirationPage from './MobileInspirationPage';

const InspirationPage = React.memo(
  ({ mobileItemsPerRow, fabricsData, toggleMobileItemsPerRow, isMobile }) => {
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const { state: filterState, actions: filterActions } = useFilters();

    const { handlePhotosLoaded } = useUrlPhotoSelection(setSelectedPhoto);

    const getCustomProps = useCallback(selectedItem => {
      if (!selectedItem) return {};

      return {
        selectedItemName:
          selectedItem.customer_first_name ||
          selectedItem.photo_full_name ||
          '',
        selectedItemDescription: selectedItem.photo_comment || '',
        showDescriptionQuoteMark: true,
        selectedItemImage: selectedItem.image_link,
        selectedItemPhotoId: selectedItem.photo_id,
        selectedItemType: 'customer-photo',
        imageProps: {
          src: selectedItem.image_link,
          alt:
            selectedItem.customer_first_name ||
            selectedItem.photo_full_name ||
            '',
        },
      };
    }, []);

    const handlePhotoClick = useCallback((photo, updateSelectedGridItem) => {
      if (isClient()) {
        const newUrl = new URL(window.location);
        newUrl.searchParams.set('photoid', photo.photo_id);
        window.history.replaceState(null, '', newUrl);
      }

      updateSelectedGridItem(photo);
    }, []);

    const { isProductIdFiltered } = useProductFilterState(
      filterState.selectedProduct
    );

    const {
      onSearchUpdate,
      resetProductSearch,
      handleProductClicked,
      handleMaterialOptionChange,
      productSearchState,
    } = useProductSearch(filterState, filterActions);

    const customerPhotosFilterActions = useCustomerPhotosFilterActions(
      filterState,
      filterActions
    );

    return (
      <div className="customer-photos__container relative">
        <MobileOnly>
          <MobileInspirationPage
            mobileItemsPerRow={mobileItemsPerRow}
            fabricsData={fabricsData}
            toggleMobileItemsPerRow={toggleMobileItemsPerRow}
            isMobile={isMobile}
          />
        </MobileOnly>

        <TabletAndDesktopOnly>
          <div className="hidden md:block">
            <div className="max-[767px]:hidden">
              <ShopByTitleNavigation page="Inspiration">
                <div className="relative">
                  <InstantSearch
                    searchClient={searchClient}
                    indexName={ALGOLIA_INDEX_NAME.PRODUCTS}
                    searchState={productSearchState}
                    onSearchStateChange={onSearchUpdate}
                  >
                    <Configure
                      hitsPerPage="24"
                      query={filterState.searchQuery}
                    />
                    <InspirationSearchBox
                      resetProductSearch={resetProductSearch}
                      onProductClicked={handleProductClicked}
                    />
                    <InspirationSearchOptions
                      fabricsData={fabricsData}
                      onSearchUpdate={onSearchUpdate}
                      onMaterialOptionChange={handleMaterialOptionChange}
                      onProductClicked={handleProductClicked}
                      isProductIdFiltered={isProductIdFiltered}
                      isMaterialOptionFiltered={
                        filterState.searchMaterialFilter
                      }
                    />
                  </InstantSearch>
                </div>
              </ShopByTitleNavigation>
            </div>

            <div className="max-[767px]:hidden pl-[3.333vw] pr-[3.333vw] [border-bottom:1px_solid_#f1f1f1]">
              <div className="flex-wrap pt-4 px-0 pb-0 md:items-start md:flex md:min-h-[100px] md:mt-0 md:mb-0">
                <InspirationFiltersTop
                  filterState={filterState}
                  filterActions={customerPhotosFilterActions}
                />
              </div>
              {filterState.selectedFiltersList.length > 0 && (
                <div className="flex flex-wrap">
                  {filterState.selectedFiltersList.map(filter => (
                    <button
                      type="button"
                      className="text-sm font-bold rounded-[30px] mt-1 mr-4 mb-4 ml-0 pt-[.4rem] px-4 pb-2 bg-brand border border-solid border-brand capitalize text-white [&_svg]:w-2 [&_svg]:cursor-pointer [&_svg_path]:fill-white flex items-center space-x-2"
                      key={`${filter.name}-${filter.filterName}`}
                      onClick={() => {
                        filter.onDeselect();
                        if (
                          filter.name === 'product' ||
                          filter.name === 'searchMaterial'
                        ) {
                          resetProductSearch();
                        }
                      }}
                    >
                      <span>{filter.filterName}</span>
                      <IconClose />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="max-[767px]:hidden">
              <div className="inspiration-page [&_.infinite-scroll-component]:!m-0 md:ml-[3.333vw] relative">
                <GridWithSidebar
                  getCustomProps={getCustomProps}
                  gridItemType="customer-photo"
                  mobileItemsPerRow={mobileItemsPerRow}
                  naturalHeightImages
                  renderGrid={({ updateSelectedGridItem }) => (
                    <CustomerPhotosGrid
                      mobileItemsPerRow={mobileItemsPerRow}
                      updateSelectedGridItem={photo =>
                        handlePhotoClick(photo, updateSelectedGridItem)
                      }
                      variant="InspirationPage"
                      onPhotoFound={handlePhotosLoaded}
                      filterState={filterState}
                      filterActions={filterActions}
                    />
                  )}
                  tabsConfig={{
                    products: true,
                    productsTitle: ' ',
                    productsHideLoadMore: true,
                  }}
                  variant="InspirationPage"
                  initialSelection={selectedPhoto}
                />
              </div>
            </div>
          </div>
        </TabletAndDesktopOnly>
      </div>
    );
  }
);

InspirationPage.propTypes = {
  mobileItemsPerRow: PropTypes.number.isRequired,
  toggleMobileItemsPerRow: PropTypes.func.isRequired,
  fabricsData: PropTypes.array,
  isMobile: PropTypes.bool,
};

export default InspirationPage;
